import {
  queryReportListing,
  mapQueryReportListing,
  queryReportCatalog,
  mapQueryReportCatalog,
  ReportListingParams,
  ReportCatalogParams,
  ReportListingItem,
  ReportCatalogItem
} from './queries'
import apiService from '../../services/api/apiService'
import { DownloadPdfParams } from '../../interfaces'
import graphqlService from '../../services/graphqlService'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export enum ReportTypes {
  dataSource = 'DATASOURCE_SUMMARY_PDF_REPORT',
  delta = 'DELTA_SUMMARY_PDF_REPORT',
  globalTables = 'TABLE_LIST',
  globalFiles = 'FILE_LIST'
}

export interface ReportConfiguration {
  id?: string
  configuration: {
    emailIDs: string[]
  }
  notificationConfiguration?: {
    emailIDs: string[]
  }
  notificationType?: string
  cron: string
  emailBody?: string
  reportType?: string
}

export interface ReportListingState {
  list: ReportListingItem[]
  count: number
}

export interface ReportCatalogState {
  list: ReportCatalogItem[]
  count: number
}

export const ACTION_REPORT_CONFIG_SAVE = 'report/save'
export const saveReportConfiguration = createAsyncThunk(
  ACTION_REPORT_CONFIG_SAVE,
  async (params: ReportConfiguration) => {
    params?.id && (await apiService.deleteReportConfiguration(params.id))
    await apiService.saveReportConfiguration(params)
  }
)

export const ACTION_REPORT_CONFIG_DELETE = 'report-settings/delete'
export const deleteReportConfiguration = createAsyncThunk(
  ACTION_REPORT_CONFIG_DELETE,
  async (id: string) => await apiService.deleteReportConfiguration(id)
)

export const ACTION_REPORT_CONFIG_FETCH = 'report/get'
export const getReportConfiguration = createAsyncThunk(
  ACTION_REPORT_CONFIG_FETCH,
  async () => await apiService.getReportConfiguration()
)

export const ACTION_REPORT_LISTING_FETCH = 'report/listing'
export const fetchReportListing = createAsyncThunk(
  ACTION_REPORT_LISTING_FETCH,
  async (params: ReportListingParams) => {
    const resultRaw = await graphqlService.execute(queryReportListing(params))
    return mapQueryReportListing(resultRaw)
  }
)

export const ACTION_REPORT_CATALOG_FETCH = 'report/catalog'
export const fetchReportCatalog = createAsyncThunk(
  ACTION_REPORT_CATALOG_FETCH,
  async (params: ReportCatalogParams) => {
    const resultRaw = await graphqlService.execute(queryReportCatalog(params))
    return mapQueryReportCatalog(resultRaw)
  }
)

interface ReportState {
  config?: ReportConfiguration
  listing: ReportListingState
  catalog: ReportCatalogState
}

export const ACTION_SEND_DOWNLOAD_PDF_REQUEST = 'report/sendDownloadPdfRequest'
export const sendDownloadPdfRequest = createAsyncThunk(
  ACTION_SEND_DOWNLOAD_PDF_REQUEST,
  async (params: DownloadPdfParams): Promise<{ data: string }> => {
    const { data } = await apiService.downloadFile(params)
    return { data }
  }
)

export const initialState: ReportState = {
  listing: {
    list: [],
    count: 0
  },
  catalog: {
    list: [],
    count: 0
  }
}

const reportSlice = createSlice({
  name: 'reportConfig',
  initialState,
  reducers: {
    resetConfig: (state) => {
      state.config = initialState.config
    },
    resetListing: (state) => {
      state.listing = initialState.listing
    },
    resetCatalog: (state) => {
      state.catalog = initialState.catalog
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReportConfiguration.fulfilled, (state, { payload }) => {
        state.config = payload[payload.length - 1]
      })
      .addCase(fetchReportListing.fulfilled, (state, { payload }) => {
        state.listing.list = payload.list
        state.listing.count = payload.count
      })
      .addCase(fetchReportCatalog.fulfilled, (state, { payload }) => {
        state.catalog.list = payload.list
        state.catalog.count = payload.count
      })
  }
})

export const { resetConfig, resetListing, resetCatalog } = reportSlice.actions
export default reportSlice.reducer
